import { state2, IStateOptions, cacheTimeMax, IStateOptions2 } from './state';
import { Inject, Injectable, inject } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { IGenericDocument } from '../document-viewer/document-viewer.component';
import { HttpClient } from '@angular/common/http';
import { AppService, DocumentAction, IDocumentMode } from './app.service';
import { IFullTextSearchArgs } from '../zoeken/zoek.service';
import { IAppGlobalData } from './global-data.service';
import { filter, first } from 'rxjs/operators';
import { FlyoutService, IFlyout } from './flyout.service';
import * as PDFOBject from 'pdfobject';
import { ICommonSearchArgs } from 'src/app/shared/common-search-args';
import { APP_BASE_HREF } from '@angular/common';


export interface ITextMenuItem {
  title: string;
  link?: string;
  execute?: () => void;
}

export interface IIconMenuItem {
  cssClass: string;
  title: string;
  command?: any;
  badgeValue?: number;
  role?: string;
  selected?: boolean;
  execute?: () => Promise<boolean>;
}

const allDocumentModes : IDocumentMode[] = [
  {title:'Document mode : Bekijken',cssclass:'fa-eye', id:DocumentAction.View},
  {title:'Document mode : Downloaden',cssclass:'fa-download',id:DocumentAction.Download},
  {title:'Document mode : Bekijken nieuw venster',cssclass:'fa-file-o',id:DocumentAction.ViewNewTab},
];


@Injectable({
  providedIn: 'root'
})
export class MenuService {

  public documentModes:IDocumentMode[];

  textMenuItems: ITextMenuItem[];
  iconMenuItems: IIconMenuItem[];
  asideMenuItems: IIconMenuItem[];

  activeTextMenuItem: ITextMenuItem;
  activeIconMenuItem: IIconMenuItem;
  activeAsideMenuItem: IIconMenuItem;

  notitiesMenuItem: IIconMenuItem;
  bladwijzersMenuItem: IIconMenuItem;
  currentUrl: string;

  constructor(private router: Router, private appService: AppService, private http: HttpClient,
    private flyoutService: FlyoutService, @Inject(APP_BASE_HREF) public baseHref: string) {

    this.textMenuItems = [
      { title: 'Startpagina', link: 'dashboard' }
    ];
    this.activeTextMenuItem = this.textMenuItems[0];

    this.iconMenuItems = [
      { cssClass: 'icon-user c2-clickable', title: 'Uw profiel', command: '/gebruiker/profiel' },
      { cssClass: 'icon-pin c2-clickable', title: 'Prikbord', command: '/prikbord' },
      { cssClass: 'icon-zoom c2-clickable', title: 'Zoeken', command: '/zoekdocumentzitting' },
      { cssClass: 'icon-onOff c2-clickable', title: 'Afmelden', execute: this.logout }
    ];

    this.notitiesMenuItem = { cssClass: 'icon-notities-dots', title: 'Toon/verberg notities', execute: this.showRecenteNotities };
    this.bladwijzersMenuItem = { cssClass: 'icon-star', title: 'Toon/verberg bladwijzers', execute: this.showRecenteBladwijzers };
    this.asideMenuItems = [
      this.notitiesMenuItem,
      this.bladwijzersMenuItem
    ];
    this.router.events.pipe(
      filter(event => event instanceof NavigationStart)
    ).subscribe(() => this.closeAllFlyouts());
  }

  appGlobalData: IAppGlobalData;
  documentModi : DocumentAction[]=[];
  documentMenuItems : IIconMenuItem[];
  documentModusItems : IDocumentMode[];
  
  setAppGlobalData(appGlobalData: IAppGlobalData) {
    this.appGlobalData = appGlobalData;
    if (this.appGlobalData.HasCebanArchief) {
      this.textMenuItems.push({ title: 'Ceban Archief', execute: () => window.open(this.appGlobalData.CebanArchiefUrl, '_blank') });
    }
    if (this.appGlobalData.PdfDownloadAllowed) {
      this.documentModi = [DocumentAction.View,DocumentAction.Download, DocumentAction.ViewNewTab];
    } else {
      this.documentModi = [DocumentAction.View,DocumentAction.ViewNewTab];
    }
    this.documentModusItems = allDocumentModes.filter(a=>this.documentModi.includes(a.id));
  }

  closeAllFlyouts() {
    this.activeAsideMenuItem = null;
    this.flyoutService.clearAll([this.recenteBladwijzersFlyout, this.recenteNotitiesFlyout]);
  }

  setActiveMenu(item: ITextMenuItem) {
    if (!item) { this.activeTextMenuItem = item; return; }
    if (item.execute) {
      item.execute();
      return false;
    }
    this.router.navigate([item.link]).then(result => {
      if (!result) { return; }
      this.activeIconMenuItem = null;
      this.activeTextMenuItem = item;
    });
    return false;
  }
  navigateToAsideMenuItem(item: IIconMenuItem) {
    if (this.activeAsideMenuItem === item) {
      this.activeAsideMenuItem = null;
      item.execute();
    } else {
      this.activeAsideMenuItem = item;
      item.execute();
    }
    return false;
  }
  navigateToIconMenuItem(item: IIconMenuItem) {
    if (item.command) {
      this.router.navigate([item.command]).then(() => {
        this.activeTextMenuItem = null;
        this.activeIconMenuItem = item;
      });
    } else if (item.execute) {
      item.execute().then(_ => {
        this.activeTextMenuItem = null;
        this.activeIconMenuItem = item;
      });
    }
  }

  private showRecenteNotities = () => {
    this.flyoutService.toggleVisible(this.recenteNotitiesFlyout, this.recenteBladwijzersFlyout);
    return Promise.resolve(true);
  }
  private showRecenteBladwijzers = () => {
    this.flyoutService.toggleVisible(this.recenteBladwijzersFlyout, this.recenteNotitiesFlyout);
    return Promise.resolve(true);
  }

  public clearFlyouts() {
    this.flyoutService.clearAll([this.recenteBladwijzersFlyout, this.recenteNotitiesFlyout]);
  }

  toggleNotities() {
    this.navigateToAsideMenuItem(this.notitiesMenuItem);
  }
  toggleBladwijzers() {
    this.navigateToAsideMenuItem(this.bladwijzersMenuItem);
  }

  urlCreator = {
    agendapunt: (id: number) => this.appService.getDocUrl(`Api/DocumentAgendapunt/GetDocumentContent?documentId=${id}`),
    zitting: (id: number) => this.appService.getDocUrl(`Api/DocumentZitting/GetDocumentContent?documentId=${id}`),
  };

  openBoaDocument = (docRef: string,
    filename: string,
    ext: string,
  ) => {
    const src = this.appService.getDocUrl(`Api/Boa/GetBijlage?docRef=${docRef}&fileName=${filename}&ext=${ext}`);
    const ispdf = ext.toLowerCase() === '.pdf';
    this.showDocument({ title: filename, src, ispdf, useinline: this.appGlobalData.PdfUseInlinePdfViewer });
  }

  openDocument(type: 'agendapunt' | 'zitting', id: number, title: string, verwijzing: string) {
    const ispdf = verwijzing.toLowerCase().endsWith('.pdf');
    const src = this.urlCreator[type](id);
    this.showDocument({ title, src, ispdf, useinline: this.appGlobalData.PdfUseInlinePdfViewer });
  }

  documentViewerVisible = false;
  currentdocument: IGenericDocument;
  showDocument(document: IGenericDocument) {

    const downloadFile = this.appService.currentDocumentMode.id === DocumentAction.Download;
    const pdfInNewTab = (!PDFOBject.supportsPDFs) && (!document.useinline) && (document.ispdf);

    if (downloadFile || pdfInNewTab) {
      window.open(document.src);
      return;
    }

    if (this.appService.currentDocumentMode.id === DocumentAction.ViewNewTab) {
      const url = this.router.serializeUrl(
        this.router.createUrlTree([`document`,'view'])
      );

      const newurl = this.baseHref + url;
      const newTab = window.open(newurl,'_blank');
      newTab.addEventListener('load',()=>{
         newTab.postMessage(document,  window.location.origin);
      })
      return;
    }

    this.documentViewerVisible = true;
    this.currentdocument = document;
  }
  retrieveBadges() {
    const url = this.appService.getUrl('api/Gebruiker/GetUserInfo');
    this.http.post<{ colorScheme: string, recenteNotitiesCount: number, pdfInTab: boolean, defaultDocumentMode:number }>(url, {}).subscribe(result => {
      this.asideMenuItems[0].badgeValue = result.recenteNotitiesCount;
      this.appService.currentDocumentMode = allDocumentModes.find(d=>d.id == result.defaultDocumentMode );
      this.appService.setTheme(result.colorScheme);
    });
  }
  decreaseRecenteNotitiesCount() {
    this.asideMenuItems[0].badgeValue--;
  }
  globalSearchNew(searchArgs: ICommonSearchArgs) {
    this.appService.routing.navigateToZoekAgendapunten(searchArgs);
  }
  globalSearchMore(searchArgs: IFullTextSearchArgs) {
    const options: IStateOptions = { allowFromCache: true, cacheTime: cacheTimeMax, concat: true };
    state2.set('searchoptions', options);
    this.router.navigate(['zoek', searchArgs]);
  }

  logout = () => {
      //eerst logout voor remove token om op de server de principal nog te kunnen raadplegen. first() immediately closes subscription
    // this.loginService.Logout().pipe(first()).subscribe(); 
    // this.loginStorageService.RemoveToken(AuthGlobals.ApplicationPath);
    const baseUrl = (<HTMLBaseElement>document.getElementById('baseElm')).href;
    this.router.navigate(['/login'], { queryParams: { returnUrl: baseUrl } });
    return Promise.resolve(true);
  }

  recenteNotitiesFlyout: IFlyout;
  addNotitieFlyout(flyout: IFlyout) {
    this.recenteNotitiesFlyout = flyout;
  }
  recenteBladwijzersFlyout: IFlyout;
  addRecenteBladwijzersFlyout(flyout: IFlyout) {
    this.recenteBladwijzersFlyout = flyout;
  }
}
